import { useState, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";

import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Stack from "@mui/material/Stack";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Switch from "@mui/material/Switch";
import RefreshIcon from "@mui/icons-material/Refresh";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import ArrowCircleUpIcon from "@mui/icons-material/ArrowCircleUp";
import ArrowCircleDownIcon from "@mui/icons-material/ArrowCircleDown";
import ViewCarouselOutlinedIcon from "@mui/icons-material/ViewCarouselOutlined";
import ViewDayOutlinedIcon from "@mui/icons-material/ViewDayOutlined";
import CloudDownloadOutlinedIcon from "@mui/icons-material/CloudDownloadOutlined";
import KeyboardDoubleArrowUpIcon from "@mui/icons-material/KeyboardDoubleArrowUp";
import KeyboardDoubleArrowDownIcon from "@mui/icons-material/KeyboardDoubleArrowDown";
import AddCircleOutlineOutlinedIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import FileCopyOutlinedIcon from "@mui/icons-material/FileCopyOutlined";
import UnsubscribeOutlinedIcon from '@mui/icons-material/UnsubscribeOutlined';
import WorkHistoryOutlinedIcon from "@mui/icons-material/WorkHistoryOutlined";

import CustomButton from "../common/CustomButton";
import DialogCustomColumn from "./DialogCustomColumn";
import { defaultFilter, TABLE_NAME } from "../../helpers";

import "./TableActionButtons.sass";


function TableActionButtons({
  handleAddNewRecord,
  handleDuplicateExistRecords,
  handleChangeLogHistory,
  tableState,
  setTableState,
  handleColumnOrder,
  handleDownloadRecodes,
  handleRefresh,
  allColumnsWithStatus,
  onToggleColumnVisibility,
  handleDeleteCoupaFile,
}) {
  const [isVisibility, setIsVisibility] = useState(null);
  const visibilityOpen = Boolean(isVisibility);
  const [isDensity, setIsDensity] = useState(false);
  const densityOpen = Boolean(isDensity);

  const { t } = useTranslation();

  const [dialog, setDialog] = useState({
    dialogType: "",
    isOpenCColumn: false,
  });

  const handleVisibility = useCallback((event) => {
    setIsVisibility(event.currentTarget);
  }, []);

  const handleVisibilityClose = () => {
    setIsVisibility(null);
  };

  const handleDensity = useCallback((event) => {
    setIsDensity(event.currentTarget);
  }, []);

  const handleDensityClose = () => {
    setIsDensity(null);
  };

  const handleDensitySmall = useCallback(() => {
    setTableState((preValue) => ({ ...preValue, density: "small" }));
    handleDensityClose();
  }, [tableState]);

  const handleDensityMedium = useCallback(() => {
    setTableState((preValue) => ({ ...preValue, density: "medium" }));
    handleDensityClose();
  }, [tableState]);

  const handleDensityLarge = useCallback(() => {
    setTableState((preValue) => ({ ...preValue, density: "large" }));
    handleDensityClose();
  }, [tableState]);

  const IconBox = ({ title, onClick, children, datatestid }) => (
    <Box className="icon-box-style">
      <IconButton
        title={title}
        onClick={onClick}
        className="custom-pending"
        data-testid={datatestid}
      >
        {children}
      </IconButton>
    </Box>
  );

  const handleCColumnClose = () => {
    setDialog({ dialogType: "", isOpenCColumn: false });
  };

  const handleCColumnOpen = (type) => {
    setDialog({ dialogType: type, isOpenCColumn: true });
  };

  const displayColumns = useMemo(() => {
    if (allColumnsWithStatus && allColumnsWithStatus.length > 0) {
      return [...allColumnsWithStatus].sort((a, b) => {
        if (a.isVisible && !b.isVisible) return -1;
        if (!a.isVisible && b.isVisible) return 1;
        return (a.priority || a.columnNumber) - (b.priority || b.columnNumber);
      });
    }
    return null;
  }, [allColumnsWithStatus]);


  return (
    <Stack sx={{ width: "100%", p: 0 }} data-testid="table-action-buttons">
      <Card>
        <CardContent>
          <Grid container alignItems="center" justifyContent="space-between" columnSpacing={2} rowSpacing={1}>
            <Grid item xs={12} md="auto">
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap rowGap={1} alignItems="center">
              {handleColumnOrder && (
                <Box className="icon-box-style">
               <IconButton
                    title={t("hide_column")}
                    id="visibility-button"
                    aria-controls={
                      visibilityOpen ? "basic-visibility-menu" : undefined
                    }
                    aria-haspopup="true"
                    aria-expanded={visibilityOpen ? "true" : undefined}
                    onClick={handleVisibility}
                    disableRipple
                    className="custom-padding"
                    data-testid="reorder-columns"
                  >
                    <ViewCarouselOutlinedIcon className="icon-style" />
                  </IconButton>

                  <Menu
                    id="basic-visibility-menu"
                    anchorEl={isVisibility}
                    open={visibilityOpen}
                    onClose={handleVisibilityClose}
                    MenuListProps={{
                      "aria-labelledby": "visibility-button",
                    }}
                    PaperProps={{ className: "paper-prop" }}
                  >
                    <Stack
                      direction="row"
                      justifyContent="flex-end"
                      alignItems="center"
                      spacing={1}
                      sx={{ mb: 1, px: 1 }}
                    >
                      <IconButton
                        onClick={() => handleCColumnOpen("custom_columns")}
                        size="small"
                      >
                        <FilterAltIcon fontSize="small" />
                      </IconButton>
                    </Stack>

                    {displayColumns && displayColumns.map((column, index) => {
                        const visibleColumns = displayColumns.filter(col => col.isVisible);
                        const visibleIndex = visibleColumns.findIndex(col => col.columnName === column.columnName);
                        const isFirst = visibleIndex === 0;
                        const isLast = visibleIndex === visibleColumns.length - 1;

                        return (
                          <MenuItem
                            key={column.columnName}
                            disableRipple
                            className="menu-item2"
                          >
                            <span>{t(TABLE_NAME[column.columnName] || column.columnName)}</span>
                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems="center"
                            >
                              <Switch
                                checked={column.isVisible}
                            onChange={() => onToggleColumnVisibility && onToggleColumnVisibility(column.columnName)}
                              />
                              <IconButton
                                size="small"
                                disabled={!column.isVisible || isFirst}
                                onClick={() => handleColumnOrder && handleColumnOrder("up", column.columnName)}
                                data-testid="column-order-up"
                              >
                                <ArrowCircleUpIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                disabled={!column.isVisible || isLast}
                                onClick={() => handleColumnOrder && handleColumnOrder("down", column.columnName)}
                                data-testid="column-order-down"
                              >
                                <ArrowCircleDownIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                disabled={!column.isVisible || isFirst}
                                onClick={() => handleColumnOrder && handleColumnOrder("top", column.columnName)}
                                data-testid="column-order-top"
                              >
                                <KeyboardDoubleArrowUpIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                disabled={!column.isVisible || isLast}
                                onClick={() => handleColumnOrder && handleColumnOrder("bottom", column.columnName)}
                                data-testid="column-order-bottom"
                              >
                                <KeyboardDoubleArrowDownIcon fontSize="small" />
                              </IconButton>
                            </Stack>
                          </MenuItem>
                        );
                      })}

                    <Stack
                      direction="row"
                      justifyContent="flex-end"
                      spacing={1}
                      className="save-stack"
                    >
                      <CustomButton
                        datatestid="save"
                        size="small"
                        onClick={() => handleCColumnOpen("save")}
                      >
                        {t("save")}
                      </CustomButton>
                    </Stack>
                  </Menu>
                </Box>
              )}

                <Box className="icon-box-style">
                  <IconButton
                    title={t("table_density")}
                    id="density-button"
                    aria-controls={densityOpen ? "basic-density-menu" : undefined}
                    aria-haspopup="true"
                    aria-expanded={densityOpen ? "true" : undefined}
                    onClick={handleDensity}
                    disableRipple
                    className="custom-padding"
                  >
                    <ViewDayOutlinedIcon className="icon-style" />
                  </IconButton>
                  <Menu
                    id="basic-density-menu"
                    anchorEl={isDensity}
                    open={densityOpen}
                    onClose={handleDensityClose}
                    MenuListProps={{
                      "aria-labelledby": "visibility-button",
                    }}
                  >
                    <MenuItem
                      onClick={handleDensitySmall}
                      disableRipple
                      className="flex-between"
                    >
                      {t("small")}
                    </MenuItem>
                    <MenuItem
                      onClick={handleDensityMedium}
                      disableRipple
                      className="flex-between"
                    >
                      {t("medium")}
                    </MenuItem>
                    <MenuItem
                      onClick={handleDensityLarge}
                      disableRipple
                      className="flex-between"
                      data-testid="density-large"
                    >
                      {t("large")}
                    </MenuItem>
                  </Menu>
                </Box>
              </Stack>
            </Grid>
            <Grid item xs={12} md>
              <Stack
                direction="row"
                spacing={1}
                flexWrap="wrap"
                useFlexGap
                rowGap={1}
                justifyContent={{ xs: "flex-start", md: "flex-end" }}
              >
                {handleAddNewRecord && (
                  <IconBox
                    title={t("add_item")}
                    onClick={handleAddNewRecord}
                    datatestid="add-new-record"
                  >
                    <AddCircleOutlineOutlinedIcon className="icon-style" />
                  </IconBox>
                )}
                {handleDuplicateExistRecords && (
                  <IconBox
                    title={t("duplicate_item")}
                    onClick={handleDuplicateExistRecords}
                    datatestid="duplicate-records"
                  >
                    <FileCopyOutlinedIcon className="icon-style" />
                  </IconBox>
                )}
                {handleChangeLogHistory && (
                  <IconBox
                    title={t("change_log_history")}
                    onClick={handleChangeLogHistory}
                    datatestid="change-log-history"
                  >
                    <WorkHistoryOutlinedIcon className="icon-style" />
                  </IconBox>
                )}

                {handleDeleteCoupaFile && (
                  <IconBox
                    title={t("delete_coupa_file")}
                    onClick={handleDeleteCoupaFile}
                    datatestid="delete-coupa-file"
                  >
                    <UnsubscribeOutlinedIcon className="icon-style" />
                  </IconBox>
                )}

                {handleDownloadRecodes && (<IconBox
                    title={t("download")}
                    onClick={handleDownloadRecodes}
                    datatestid="download-records"
                  >
                    <CloudDownloadOutlinedIcon className="icon-style" />
                  </IconBox>)}

                {handleRefresh && (
                  <IconBox
                    title={t("refresh")}
                    onClick={() => handleRefresh(tableState.currentPage, defaultFilter) }
                    datatestid="refresh"
                  >
                    <RefreshIcon className="icon-style" />
                  </IconBox>)}
              </Stack>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {dialog.isOpenCColumn && (
        <DialogCustomColumn
          open={dialog.isOpenCColumn}
          displayColumns={displayColumns}
          handleClose={handleCColumnClose}
          dialogType={dialog.dialogType}
        />
      )}
    </Stack>
  );
}

export default TableActionButtons;
